import React, { useMemo, useState } from 'react';
import { useAdminCategories } from '../../hooks/useAdminCategories.js';
import { useLucideIcons } from '../../hooks/usePageAssets.js';

const formatPercentage = (value) => {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) {
    return '0.00%';
  }
  return `${parsed.toFixed(2)}%`;
};

const CategoriesSection = ({ onNotify }) => {
  const { items, loading, error, refresh, saveCategory, deleteCategory } = useAdminCategories();
  const [search, setSearch] = useState('');
  const [editorState, setEditorState] = useState({ open: false, record: null, saving: false });

  useLucideIcons([items.length, search, editorState.open]);

  const filteredItems = useMemo(() => {
    if (!search) {
      return items;
    }
    const query = search.toLowerCase();
    return items.filter((category) => {
      const name = category.nombre?.toLowerCase() || '';
      const description = category.descripcion?.toLowerCase() || '';
      return name.includes(query) || description.includes(query);
    });
  }, [items, search]);

  const handleOpenModal = (record = null) => {
    setEditorState({
      open: true,
      saving: false,
      record: record
        ? { ...record }
        : { nombre: '', descripcion: '', ajuste_forecast_pct: 0, activo: true }
    });
  };

  const handleSave = async (values) => {
    setEditorState((prev) => ({ ...prev, saving: true }));
    try {
      await saveCategory(values);
      setEditorState({ open: false, record: null, saving: false });
      refresh();
      onNotify?.({
        type: 'success',
        message: values?.id ? 'Categoría actualizada correctamente.' : 'Categoría creada correctamente.'
      });
    } catch (err) {
      setEditorState((prev) => ({ ...prev, saving: false }));
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo guardar la categoría.' });
    }
  };

  const handleDelete = async (record) => {
    const confirmed = window.confirm(`¿Eliminar la categoría "${record.nombre}"?`);
    if (!confirmed) {
      return;
    }
    try {
      await deleteCategory(record.id);
      refresh();
      onNotify?.({ type: 'success', message: 'Categoría eliminada.' });
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo eliminar la categoría.' });
    }
  };

  const closeModal = () => {
    setEditorState({ open: false, record: null, saving: false });
  };

  return (
    <section id="categories-section" className="admin-section active">
      <div className="section-header">
        <h2 className="section-title">Categorías</h2>
        <div className="section-actions">
          <div className="search-container">
            <span className="search-icon"><i data-lucide="search" /></span>
            <input
              type="text"
              className="form-input"
              placeholder="Buscar por nombre o descripción..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search ? (
              <button type="button" className="btn-secondary" onClick={() => setSearch('')}>
                Limpiar
              </button>
            ) : null}
          </div>
          <button className="btn-primary" type="button" onClick={() => handleOpenModal()}>
            <span className="btn-icon"><i data-lucide="plus" /></span>
            Nueva Categoría
          </button>
        </div>
      </div>
      <div className="data-table-container">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <div className="loading-text">Cargando categorías...</div>
          </div>
        ) : (
          <table className="data-table" id="categories-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Descripción</th>
                <th>Ajuste forecast base</th>
                <th>Artículos vinculados</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr>
                  <td colSpan={6} className="empty-state">
                    <div className="empty-state-icon"><i data-lucide="alert-triangle" /></div>
                    <div className="empty-state-text">{error}</div>
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty-state">
                    <div className="empty-state-icon"><i data-lucide="layers" /></div>
                    <div className="empty-state-text">No hay categorías configuradas.</div>
                    <div className="empty-state-subtext">Crea tu primera categoría para comenzar.</div>
                  </td>
                </tr>
              ) : (
                filteredItems.map((category) => (
                  <tr key={category.id}>
                    <td>{category.nombre}</td>
                    <td>{category.descripcion || 'Sin descripción'}</td>
                    <td>{formatPercentage(category.ajuste_forecast_pct)}</td>
                    <td><span className="process-count">{category.articulos_count || 0}</span></td>
                    <td>
                      <span className={`status-badge ${category.activo ? 'status-active' : 'status-inactive'}`}>
                        {category.activo ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="action-btn edit" type="button" onClick={() => handleOpenModal(category)}>
                          <i data-lucide="edit" />
                        </button>
                        <button className="action-btn delete" type="button" onClick={() => handleDelete(category)}>
                          <i data-lucide="trash-2" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      {editorState.open ? (
        <CategoryModal state={editorState} onClose={closeModal} onSubmit={handleSave} />
      ) : null}
    </section>
  );
};

const CategoryModal = ({ state, onClose, onSubmit }) => {
  const [formValues, setFormValues] = useState(() => ({
    ...state.record,
    nombre: state.record?.nombre || '',
    descripcion: state.record?.descripcion || '',
    ajuste_forecast_pct: state.record?.ajuste_forecast_pct ?? 0,
    activo: state.record?.activo ?? true
  }));

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({ ...formValues, id: state.record?.id || null });
  };

  return (
    <div className="modal" style={{ display: 'block' }}>
      <div className="modal-content" style={{ maxWidth: '520px' }}>
        <div className="modal-header">
          <h3>{state.record?.id ? 'Editar Categoría' : 'Nueva Categoría'}</h3>
          <button className="modal-close" type="button" onClick={onClose}>
            <i data-lucide="x" />
          </button>
        </div>
        <form className="modal-body form-grid" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Nombre</label>
            <input
              type="text"
              className="form-input"
              name="nombre"
              value={formValues.nombre}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Descripción</label>
            <textarea
              className="form-input"
              name="descripcion"
              value={formValues.descripcion}
              onChange={handleChange}
              rows={3}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Ajuste forecast base (%)</label>
            <input
              type="number"
              className="form-input"
              name="ajuste_forecast_pct"
              value={formValues.ajuste_forecast_pct ?? 0}
              onChange={handleChange}
              step="0.01"
              min="-100"
              max="500"
            />
          </div>
          <div className="form-group checkbox-group">
            <label className="checkbox-item">
              <input
                type="checkbox"
                name="activo"
                checked={formValues.activo}
                onChange={handleChange}
              />
              <span className="checkmark" />
              <span className="checkbox-label">Categoría activa</span>
            </label>
          </div>
          <div className="modal-footer">
            <button className="btn-secondary" type="button" onClick={onClose} disabled={state.saving}>
              Cancelar
            </button>
            <button className="btn-primary" type="submit" disabled={state.saving}>
              {state.saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoriesSection;
